import { useState, useEffect } from 'react';
import type { MaterialGuideline, WasteStreamType } from '@calwmp/shared';

interface MaterialGuidelinesProps {
  materialType: WasteStreamType;
}

/**
 * Material-specific guidelines component (T111)
 * Displays guidance for waste diversion by material type
 */
export function MaterialGuidelines({ materialType }: MaterialGuidelinesProps) {
  const [guideline, setGuideline] = useState<MaterialGuideline | null>(null);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchGuideline = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/compliance/materials/guidelines?material=${materialType}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch guidelines');
        }

        const data = await response.json();
        setGuideline(data.guideline);
      } catch (err) {
        console.error('Failed to load guidelines:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGuideline();
  }, [materialType]);

  if (loading) {
    return <div className="text-muted">Loading guidelines...</div>;
  }

  if (!guideline) {
    return null;
  }

  return (
    <div className="card bg-light border-info">
      <div className="card-body p-3">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-info mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <strong className="text-info">
              {materialType.charAt(0).toUpperCase() + materialType.slice(1)} Guidelines
            </strong>
            <span className="ml-2 badge badge-info">
              {guideline.diversionPotential}% diversion potential
            </span>
          </div>
          <svg
            className={`w-5 h-5 text-info transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {isExpanded && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-sm text-muted mb-3">{guideline.description}</p>

            <div className="mb-3">
              <strong className="text-sm">Recommended Methods:</strong>
              <div className="flex flex-wrap gap-2 mt-1">
                {guideline.recommendedDiversionMethods.map((method) => (
                  <span key={method} className="badge badge-success">
                    {method.charAt(0).toUpperCase() + method.slice(1)}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <strong className="text-sm">Typical Facilities:</strong>
              <ul className="text-sm mt-1 ml-4">
                {guideline.typicalFacilities.map((facility, idx) => (
                  <li key={idx} className="text-muted">
                    {facility}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <strong className="text-sm">Best Practices:</strong>
              <ul className="text-sm mt-1 ml-4">
                {guideline.bestPractices.map((practice, idx) => (
                  <li key={idx} className="text-muted">
                    {practice}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}